import { useEffect, useRef, useState } from "react";
import { Loader2, RotateCw, History, X } from "lucide-react";
import { getResume, saveResume, clearResume } from "../lib/resume";
import { fmtDuration } from "../lib/api";

const qualityLabel = (h) => (h >= 2160 ? "4K" : h >= 1440 ? "1440p" : h >= 1080 ? "1080p HD" : h >= 720 ? "720p HD" : h ? `${h}p` : "");

export const VideoPlayer = ({ id, src, poster, onPlay }) => {
  const ref = useRef(null);
  const lastSave = useRef(0);
  const [quality, setQuality] = useState("");
  const [buffering, setBuffering] = useState(false);
  const [error, setError] = useState(false);
  const [resumed, setResumed] = useState(null);

  useEffect(() => {
    if (!resumed) return undefined;
    const t = setTimeout(() => setResumed(null), 8000);
    return () => clearTimeout(t);
  }, [resumed]);

  const onMeta = (e) => {
    const v = e.currentTarget;
    setQuality(qualityLabel(v.videoHeight));
    const saved = getResume(id);
    if (saved && saved.t > 5 && saved.t < (v.duration || 0) - 15) {
      v.currentTime = saved.t;
      setResumed(saved.t);
    }
  };

  const persist = (force = false) => {
    const v = ref.current;
    if (!v || !id) return;
    const now = Date.now();
    if (!force && now - lastSave.current < 4000) return;
    lastSave.current = now;
    saveResume(id, v.currentTime, v.duration);
  };

  const restart = () => {
    const v = ref.current;
    if (v) v.currentTime = 0;
    clearResume(id);
    setResumed(null);
  };

  useEffect(() => {
    const onKey = (e) => {
      const v = ref.current;
      if (!v || ["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) return;
      if (e.key === " " || e.key === "k") { e.preventDefault(); v.paused ? v.play() : v.pause(); }
      if (e.key === "ArrowRight") { e.preventDefault(); v.currentTime = Math.min(v.duration || 0, v.currentTime + 10); }
      if (e.key === "ArrowLeft") { e.preventDefault(); v.currentTime = Math.max(0, v.currentTime - 10); }
      if (e.key === "f") document.fullscreenElement ? document.exitFullscreen() : v.requestFullscreen?.();
      if (e.key === "m") v.muted = !v.muted;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const retry = () => {
    const v = ref.current;
    if (!v) return;
    const t = v.currentTime;
    setError(false);
    v.load();
    v.currentTime = t;
    v.play().catch(() => {});
  };

  return (
    <div className="relative flex justify-center bg-black" data-testid="video-player-wrap">
      <video
        ref={ref}
        src={src}
        poster={poster}
        controls
        playsInline
        preload="auto"
        controlsList="nodownload"
        onPlay={onPlay}
        onLoadedMetadata={onMeta}
        onTimeUpdate={() => persist()}
        onPause={() => persist(true)}
        onEnded={() => clearResume(id)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onError={() => setError(true)}
        className="aspect-video max-h-[80vh] w-full bg-black object-contain"
        data-testid="video-player"
      />
      {resumed !== null && (
        <div className="absolute bottom-16 left-3 flex items-center gap-3 border border-rose-900/50 bg-black/85 px-4 py-2 text-xs text-white backdrop-blur" data-testid="resume-banner">
          <History size={14} className="text-rose-400" />
          <span>Continuando de <b className="font-mono-d">{fmtDuration(resumed)}</b></span>
          <button onClick={restart} className="uppercase tracking-widest text-rose-300 hover:text-white" data-testid="resume-restart-button">Do início</button>
          <button onClick={() => setResumed(null)} aria-label="Fechar" className="text-slate-400 hover:text-white" data-testid="resume-close-button"><X size={14} /></button>
        </div>
      )}
      {quality && (
        <span className="font-mono-d pointer-events-none absolute right-3 top-3 bg-rose-600 px-2 py-1 text-[10px] font-medium tracking-widest text-white" data-testid="video-quality-badge">{quality}</span>
      )}
      {buffering && !error && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center" data-testid="video-buffering">
          <Loader2 size={44} className="animate-spin text-rose-500" />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-black/80 text-center" data-testid="video-error">
          <p className="text-white">Não foi possível carregar o vídeo.</p>
          <button onClick={retry} className="flex items-center gap-2 bg-rose-600 px-5 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white hover:bg-rose-500" data-testid="video-retry-button">
            <RotateCw size={14} /> Tentar novamente
          </button>
        </div>
      )}
    </div>
  );
};
